import React from 'react';
import { series } from '../data/evidence.js';

// Three small charts, inline SVG, every point from data/evidence.js (pinned to the record by
// evidence.test.js). One scale per chart; labels only for values the chart reaches; colours from
// the site tokens so they read on the page's own ground.
// Each chart's viewBox width matches its column, so text sized in viewBox units renders the same
// on the page for the wide chart and the two narrow ones (reviewer, PR #29).
const H = 190, PAD = { l: 42, r: 10, t: 16, b: 30 };

function Bars({ s, fmt, fmtLast = fmt, maxV, W = 360 }) {
  const pts = s.points; const n = pts.length;
  const iw = W - PAD.l - PAD.r, ih = H - PAD.t - PAD.b;
  const bw = iw / n * 0.7, gap = iw / n;
  const y = (v) => PAD.t + ih - (v / maxV) * ih;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${s.title}: ${pts.map(([k, v]) => `${k} ${fmt(v)}`).join(', ')}`}>
      {[0, 0.5, 1].map((f) => <line key={f} x1={PAD.l} x2={W - PAD.r} y1={y(maxV * f)} y2={y(maxV * f)} className="grid" />)}
      {[0, 0.5, 1].map((f) => <text key={f} x={PAD.l - 6} y={y(maxV * f) + 4} textAnchor="end" className="tick">{fmt(maxV * f)}</text>)}
      {pts.map(([k, v], i) => (
        <g key={k}>
          <rect x={PAD.l + i * gap + (gap - bw) / 2} y={y(v)} width={bw} height={PAD.t + ih - y(v)} className={i === n - 1 ? 'bar last' : 'bar'} />
          {(n <= 4 || i === 0 || i === n - 1 || k % 5 === 0) && <text x={PAD.l + i * gap + gap / 2} y={H - 10} textAnchor="middle" className="tick">{k}</text>}
        </g>
      ))}
      <text x={PAD.l + (n - 1) * gap + gap / 2} y={y(pts[n - 1][1]) - 5} textAnchor="middle" className="label">{fmtLast(pts[n - 1][1])}</text>
    </svg>
  );
}

function Curve({ s, fmt, maxX, maxV, W = 360, labelAt }) {
  const pts = s.points;
  const mark = pts.find(([k]) => k === labelAt);
  const iw = W - PAD.l - PAD.r, ih = H - PAD.t - PAD.b;
  const x = (k) => PAD.l + (k / maxX) * iw, y = (v) => PAD.t + ih - (v / maxV) * ih;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${s.title}: ${pts.map(([k, v]) => `${k} ${k === 1 ? 'year' : 'years'} ${fmt(v)}`).join(', ')}`}>
      {[0, 0.5, 1].map((f) => <line key={f} x1={PAD.l} x2={W - PAD.r} y1={y(maxV * f)} y2={y(maxV * f)} className="grid" />)}
      {[0, 0.5, 1].map((f) => <text key={f} x={PAD.l - 6} y={y(maxV * f) + 4} textAnchor="end" className="tick">{fmt(maxV * f)}</text>)}
      <polyline className="line" points={pts.map(([k, v]) => `${x(k)},${y(v)}`).join(' ')} />
      {pts.map(([k, v]) => <circle key={k} cx={x(k)} cy={y(v)} r="3.5" className="dot" />)}
      {pts.map(([k]) => <text key={k} x={x(k)} y={H - 10} textAnchor="middle" className="tick">{k}</text>)}
      {mark && <text x={x(mark[0]) + 8} y={y(mark[1]) - 8} className="label">{`${mark[1]}% at ${mark[0]} years`}</text>}
    </svg>
  );
}

export default function EvidenceCharts() {
  const M = (v) => `${(v / 1e6).toFixed(v >= 1e6 ? 1 : 0)}M`;
  const M2 = (v) => `${(v / 1e6).toFixed(2)}M`;   // the last bar's label matches the tile above it (5.67M)
  const P = (v) => `${Math.round(v)}%`;
  return (
    <div className="charts">
      <figure className="chart wide">
        <Bars s={series.applications} fmt={M} fmtLast={M2} maxV={6e6} W={480} />
        <figcaption><b>{series.applications.title}</b><span>{series.applications.source}</span></figcaption>
      </figure>
      <figure className="chart">
        <Bars s={series.solo} fmt={P} maxV={40} W={220} />
        <figcaption><b>{series.solo.title}</b><span>{series.solo.source}</span></figcaption>
      </figure>
      <figure className="chart">
        <Curve s={series.survival} fmt={P} maxX={15} maxV={100} W={220} labelAt={5} />
        <figcaption><b>{series.survival.title}</b><span>{series.survival.source}</span></figcaption>
      </figure>
    </div>
  );
}
